import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import moreIcon from "../../assets/icons/r-arrow.svg";
import { fetchResponseAdapter } from '../../adapters/fetchAdapter';
import { getAuthToken, unsetAuth } from '../../helpers/authHelper';
import useCallApiAndLoad from '../../hooks/useCallApiAndLoad';
import { logoutAction } from '../../redux/features/auth';
import { AppStore } from '../../redux/store';
import { PaymentList } from './models/investorModel';
import { getInvestorPaymentsEndpoint } from './services/investorService';

const InvestorPayments = () => {
  const { country } = useSelector((store: AppStore) => store.country);
  const user = useSelector((store: AppStore) => store.auth);
  const token = getAuthToken(user);
  const [data, setData] = useState<Array<PaymentList>>([]);
  const [checkAuto, setCheckAuto] = useState<boolean>(false);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(true);

  const { isLoading, callEndpoint } = useCallApiAndLoad();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  useEffect(() => {
    const loadPayments = async () => {
      setIsLoadingData(true);
      try {
        const { status, data } = await callEndpoint(
          getInvestorPaymentsEndpoint(token)
        );
        if (status === 401) {
          dispatch(logoutAction({}));
          unsetAuth();
          return navigate(`/app/${country}/auth/login`);
        }
        if (status === 200) {
          const response = fetchResponseAdapter(data);
          setData(response.data);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoadingData(false);
      }
    };
    loadPayments();
  }, []);

  const gotToMarket = () => {
    window.open(`/${country}/venta/casas-y-apartamentos/proyectos`, '_self');
  }

  return (
    <div className="container mx-auto px-4 xl:px-0">
      <div className="w-full pt-10 pb-20">
        <div className="w-full mt-6 mb-2">
          <h1 className="text-blackGrayScale text-2xl lg:text-2xl font-semibold text-left tracking-wide">
            Gestiona tus proyectos de inversión
          </h1> 
        </div>
        <div className="w-full mb-6 mt-2">
          <p className="text-lg font-medium text-gray600 mb-2">Inversiones</p>
        </div>
       
        <div className="w-full my-8 xl:mt-20 mt-12">
          <div className='grid md:grid-cols-2 xl:grid-cols-3 gap-6'>
            {
              isLoadingData ? (
                // Estado de carga - Skeleton
                [...Array(3)].map((_, index) => (
                  <div key={index} className="card-property pt-4 px-4 animate-pulse">
                    <div className="flex justify-between flex-wrap">
                      <div className="w-3/4 pr-2">
                        <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
                        <div className="h-4 bg-gray-200 rounded w-full"></div>
                      </div>
                      <div className="w-1/4">
                        <div className="w-full h-20 bg-gray-200 rounded"></div>
                      </div>
                      <div className="w-full mt-3">
                        <div className="h-4 bg-gray-200 rounded w-full"></div>
                      </div>
                      <div className="w-full mt-1">
                        <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                      </div>
                      <div className="w-full mt-1">
                        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                      </div>
                      <div className="w-full py-3 flex justify-center items-center border-t border-[#E0E0E0] mt-4">
                        <div className="h-4 bg-gray-200 rounded w-24"></div>
                      </div>
                    </div>
                  </div>
                ))
              ) : data && data.length > 0 ? (
                // Datos existentes
                data.map((items, index) => {
                  return (
                    <div key={index} className="card-property pt-4 px-4">
                      <div className="flex justify-between flex-wrap">
                        <div className="w-3/4 pr-2">
                          <h2 className="font-medium text-lg truncate text-blackGrayScale leading-tight">
                            {items.model_name}
                          </h2>
                          
                          <p className='text-sm text-[#616161]'>
                            {items?.project_address}
                          </p>
                        </div>
                        <div className="w-1/4">
                          <img
                            className="w-full h-20 object-cover object-center rounded"
                            src={items?.project_cover}
                            alt={items.model_name}
                          />
                        </div>
                        <div className="w-full mt-3">
                          <p className="text-gray600 text-sm">
                            <span className="font-semibold">Fecha estimada de entrega: </span>
                            {items?.estimate_delivered_at}
                          </p>
                        </div>
                        <div className="w-full mt-1">
                          <p className="text-gray600 text-sm">
                            <span className="font-semibold">Valor total:</span> {items?.unit_price}
                          </p>
                        </div>
                        <div className="w-full mt-1">
                          <p className="text-gray600 text-sm">
                            <span className="font-semibold">Unidad:</span> {items?.unit_name}
                          </p>
                        </div>
                        
                        <div className="w-full py-3 flex justify-center items-center border-t border-[#E0E0E0] mt-4">
                          <Link
                            to={`/app/${country}/l/ld/make-payment/${items.id}`}
                            onClick={() => {
                              window.scrollTo({
                                top: 0,
                                left: 0,
                                behavior: "smooth",
                              });
                            }}
                          >
                            <button className="text-blackGrayScale font-medium text-sm hover:text-orange-600 transition-colors">
                              Ver detalle
                            </button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  )
                })
              ) : (
                // Estado sin datos - Diseño elegante
                <div className="col-span-full flex flex-col items-center justify-center py-16 px-4">
                  <div className="w-32 h-32 mb-6 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                    <svg 
                      className="w-16 h-16 text-gray-400" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={1.5} 
                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" 
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-blackGrayScale mb-2">
                    No tienes inversiones activas
                  </h3>
                  <p className="text-[#757575] text-center max-w-md mb-6">
                    Parece ser que no tienes inversiones. Explora los proyectos disponibles y comienza a invertir.
                  </p>
                  <button
                    onClick={gotToMarket}
                    className="flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-all duration-300 hover:shadow-lg"
                  >
                    Ver proyectos disponibles
                    <svg 
                      className="w-4 h-4" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M9 5l7 7-7 7" 
                      />
                    </svg>
                  </button>
                </div>
              )
            }
          </div>
        </div>
      </div>
    </div>
  )
}

export default InvestorPayments